import type { RouteLocationNormalized, RouteLocationRaw, Router } from 'vue-router'
import type { Workspace } from '@shared/domain/permissions'
import { P } from '@shared/domain/permissions'
import { AnchorStatus, SupplierStatus } from '@shared/domain/status'
import { useAuthStore } from '@shared/stores/auth'
import { useOrgStore } from '@shared/stores/org'

const SUBMITTED_STATUSES: string[] = [
  AnchorStatus.SUBMITTED,
  SupplierStatus.PENDING_BANK_REVIEW,
  SupplierStatus.SUPPLIER_REJECTED,
]

/** Where an inactive organisation lands: the wizard, or the status tracker once submitted. */
export function onboardingLocation(workspace: Workspace): RouteLocationRaw {
  const auth = useAuthStore()
  const org = useOrgStore()
  const submitted = SUBMITTED_STATUSES.includes(org.activationStatus ?? '')
  if (workspace === 'anchor') {
    return !submitted && auth.can(P.ANCHOR_ONBOARDING_EDIT)
      ? { name: 'anchor.onboarding' }
      : { name: 'anchor.onboarding.status' }
  }
  if (workspace === 'supplier') {
    return !submitted && auth.can(P.SUPPLIER_REGISTRATION_EDIT)
      ? { name: 'supplier.register' }
      : { name: 'supplier.register.status' }
  }
  return { name: 'ops.dashboard' }
}

/** After login, `/` goes to the workspace home, or to onboarding if not yet active. */
export function homeLocation(): RouteLocationRaw {
  const auth = useAuthStore()
  const org = useOrgStore()
  const ws = auth.workspace
  if (!ws) return { name: 'login' }
  if (!org.isActive) return onboardingLocation(ws)
  return { name: `${ws}.dashboard` }
}

function routeWorkspace(to: RouteLocationNormalized): Workspace | undefined {
  return [...to.matched].reverse().find((r) => r.meta.workspace)?.meta.workspace
}

/** Guards run in this order on every navigation: auth → workspace → onboarding → permission. */
export function installGuards(router: Router) {
  router.beforeEach(async (to) => {
    const auth = useAuthStore()
    const org = useOrgStore()
    const requiresAuth = to.matched.some((r) => r.meta.requiresAuth)

    if (requiresAuth || to.name === 'login') await auth.ensureSession()

    // 1. Auth guard: no session → login, keeping the redirect query
    if (requiresAuth && !auth.isAuthenticated) {
      return { name: 'login', query: { redirect: to.fullPath } }
    }
    if (to.name === 'login' && auth.isAuthenticated) return homeLocation()
    if (!requiresAuth) return true
    if (to.name === 'home') return homeLocation()

    // 2. Workspace guard: a supplier cannot open /anchor/* or /ops/*
    const ws = routeWorkspace(to)
    if (ws && ws !== auth.workspace) return homeLocation()

    // 3. Onboarding guard: inactive orgs only reach onboarding/registration and the status tracker
    if (!org.isActive && !to.matched.some((r) => r.meta.allowBeforeActivation)) {
      return onboardingLocation(auth.workspace ?? 'ops')
    }

    // 4. Permission guard: wrong actor type or missing permission → 403
    const actor = auth.user?.actorType
    const wrongActor = to.matched.some(
      (r) => r.meta.actorTypes && (!actor || !r.meta.actorTypes.includes(actor)),
    )
    const missing =
      wrongActor || to.matched.some((r) => r.meta.permission && !auth.can(r.meta.permission))
    if (missing) return { name: 'forbidden', query: { from: to.fullPath } }

    return true
  })
}
